import { ReactComponent as InfoIcon } from 'assett/icons/info_icon.svg';
import SelectGeneric from 'components/commons/form/SelectGeneric';
import dayjs, { Dayjs } from 'dayjs';
import { getPrescriptions } from 'feature/claims/claimSlice';
import useDevice from 'hooks/DesktopDevice.hook';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import {
	Box,
	Button,
	ButtonLink,
	Checkbox,
	DatePicker,
	FormControl,
	Stack,
	StackRadio,
	Typography,
} from 'styles';
import palette from 'styles/theme/Palette';
import { IClaim, IPrescription } from 'types/Claim';
import { PageProps } from 'types/newClaimPageType';
import { useTranslation } from 'utils/translations';
import { formatZuluForBE, monthDiff } from 'utils/utils';
import NewClaimErrorMessages from './components/NewClaimErrorMessages';
import Prescription from './components/Prescription';

type IDoctorType = 'Optician' | 'Optometrist' | 'Ophthalmologist';

const Page1: FC<PageProps> = props => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: 'dbLabels',
	});
	const { isDesktop } = useDevice();
	const today = new Date();
	const dispatch = useDispatch<AppDispatch>();
	const { formData, setFormData, submitPage } = props;
	const { doctorTypes } = useSelector((store: RootState) => store.claim);
	const { member } = useSelector((store: RootState) => store.member);
	const [checkFields, setCheckFields] = useState<boolean>(false);
	const [newPresCompleted, setNewPresCompleted] = useState<boolean>(false);
	const [oldPresCompleted, setOldPresCompleted] = useState<boolean>(false);
	const [newPresErrorPresent, setNewPresErrorPresent] = useState<boolean>(false);
	const [oldPresErrorPresent, setOldPresErrorPresent] = useState<boolean>(false);
	const [isPrescriptionChanged, setIsPrescriptionChanged] = useState<boolean>(
		formData.rxChangedWithoutOldPrescription || false,
	);
	const showOldPrescription =
		formData.rxChangeAttestation === 1 && !isPrescriptionChanged;

	const [doctorType, setDoctorType] = useState<IDoctorType | 'reset'>(
		(formData.newPrescriptionContent?.doctorTypology as IDoctorType) || 'reset',
	);
	const [prescriptionDate, setPrescriptionDate] = useState<
		Dayjs | null | string
	>(formData.newPrescriptionContent?.prescriptionDate || null);
	const [errorDate, setErrorDate] = useState<boolean>(false);
	const [errorDoctorType, setErrorDoctorType] = useState<boolean>(false);
	const [errorDateMessage, setErrorDateMessage] = useState<string>(
		'prescriptionDateError',
	);

	const isDoctorAllowed = member?.rxChangeConfiguration.allowedDoctors
		.map(el => el.name)
		.includes(doctorType);

	const isDoctorInvalid =
		doctorType !== 'reset' &&
		(errorDoctorType ||
			(member?.rxChangeConfiguration.doctorWarningControl && !isDoctorAllowed));

	const isDateInvalid =
		errorDate ||
		(member?.rxChangeConfiguration.prescriptionWarningControl &&
			member?.rxChangeConfiguration.validityPrescriptionMonth !== null &&
			today >= dayjs(prescriptionDate).toDate() &&
			monthDiff(dayjs(prescriptionDate).toDate(), today) >=
				member?.rxChangeConfiguration.validityPrescriptionMonth);

	const isRxCheckAvoided =
		formData.rxChangeAttestation === 0 && member?.rxChangeConfiguration.rxCheck;

	const disableProceed =
		isDateInvalid ||
		isDoctorInvalid ||
		!formData.prescriptionConsent ||
		isRxCheckAvoided;

	useEffect(() => {
		scrollWithError();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [errorDate, errorDoctorType]);

	const scrollWithError = () => {
		if (
			errorDate ||
			errorDoctorType ||
			(showOldPrescription && !oldPresCompleted)
		) {
			const box = document.getElementById('page1-scroll');
			if (box) {
				if (formData.rxChangeAttestation === 0) {
					box.scrollTo({ top: 150, behavior: 'smooth' });
				} else if (formData.rxChangeAttestation === 1) {
					const topValue = isPrescriptionChanged
						? 220
						: !oldPresCompleted
							? 220
							: 710;
					box.scrollTo({ top: topValue, behavior: 'smooth' });
				}
			}
		}
	};

	const handleChangeRadio = (value: string) => {
		dispatch(getPrescriptions({}));
		setErrorDate(false);
		setErrorDoctorType(false);
		setIsPrescriptionChanged(false);
		setFormData((prev: IClaim) => ({
			...prev,
			rxChangedWithoutOldPrescription: Number(value) === 1 ? false : null,
			rxChangeAttestation: Number(value) as 0 | 1,
		}));
	};

	const handleSubmit = () => {
		scrollWithError();

		//controllo per vedere se si tratta di una data futura
		const isNotValidDate = dayjs(prescriptionDate).isAfter(dayjs());
		if (
			newPresCompleted &&
			doctorType !== 'reset' &&
			prescriptionDate &&
			!isNotValidDate &&
			(oldPresCompleted || !showOldPrescription)
		) {
			setCheckFields(false);
			submitPage!();
		} else {
			if (!doctorType || doctorType === 'reset') {
				setErrorDoctorType(true);
			}
			//se la data non è stata inserita
			if (!prescriptionDate) {
				setErrorDate(true);
				setErrorDateMessage('prescriptionDateError');
			}

			//se la data inserita è futura
			if (isNotValidDate) {
				setErrorDate(true);
				setErrorDateMessage('prescriptionDateErrorAfter');
			}
			setCheckFields(true);
		}
	};

	return (
		<>
			<Stack gap="10px">
				<Typography variant="title6">
					{tClientLabels('newClaim.selectedPage.sectionTitile.enterPrescription')}
				</Typography>
				{member?.rxChangeConfiguration.rxCheck && (
					<StackRadio
						onChange={(_, value) => handleChangeRadio(value)}
						value={formData.rxChangeAttestation ?? null}
						label={tClientLabels('newClaim.prescription.hasOldPrescriptionQ')}
						options={[
							{
								label: tClientLabels(`newClaim.prescription.hasOldPrescriptionA.yes`),
								value: 1,
							},
							{
								label: tClientLabels(`newClaim.prescription.hasOldPrescriptionA.no`),
								value: 0,
							},
						]}
						variant="transparent"
					/>
				)}
			</Stack>
			{formData.rxChangeAttestation === 1 && (
				<Stack sx={{ mt: '20px', ml: '-6px' }}>
					<Checkbox
						label={tClientLabels('newClaim.prescription.prescriptionChanged')}
						checked={isPrescriptionChanged}
						onChange={(_, value) => {
							setIsPrescriptionChanged(value);
							setFormData((prev: IClaim) => ({
								...prev,
								oldPrescriptionContent: value
									? undefined
									: (prev.oldPrescriptionContent as IPrescription),
								rxChangedWithoutOldPrescription: value,
							}));
						}}
					/>
				</Stack>
			)}
			<Stack mt="20px" gap={'30px'}>
				{(formData.rxChangeAttestation === 0 ||
					formData.rxChangeAttestation === 1) && (
					<>
						{showOldPrescription && (
							<Prescription
								presFilled={oldPresCompleted}
								key="old"
								label="newClaim.prescription.oldPrescriptionContent"
								data={formData.oldPrescriptionContent!}
								isOldPrescription={true}
								setData={(value: IPrescription) => {
									setFormData((prev: IClaim) => ({
										...prev,
										oldPrescriptionContent: value,
									}));
								}}
								checkFields={checkFields}
								setRequiredFilled={setOldPresCompleted}
								setErrorPresent={setOldPresErrorPresent}
							/>
						)}
						<Stack direction={'column'} gap={'20px'}>
							<Stack
								flexDirection={'column'}
								mt={
									formData.rxChangeAttestation === 0 ||
									(formData.rxChangeAttestation === 1 && isPrescriptionChanged)
										? 0
										: 1.25
								}
								mb={'10px'}
							>
								<Typography
									variant="bodyLink"
									mt={formData.rxChangeAttestation === 1 ? '30px' : '20px'}
									mb={'10px'}
								>
									{tClientLabels(
										formData.rxChangeAttestation === 1
											? 'newClaim.prescription.newPrescriptionContent'
											: 'newClaim.prescription.Prescription',
									)}
								</Typography>

								<Stack flexDirection={'row'}>
									<Stack gap={'8px'} sx={{ width: { sm: 340, lg: 368 } }}>
										<SelectGeneric
											action={(payload: string) => {
												setDoctorType(payload as IDoctorType);
												setFormData((prev: IClaim) => ({
													...prev,
													newPrescriptionContent: {
														...prev.newPrescriptionContent,
														doctorTypology: payload,
													} as IPrescription,
												}));
												setErrorDoctorType(false);
											}}
											items={doctorTypes.map(type => ({
												value: type.name,
												text: tDbLabels(type.label),
											}))}
											name={'doctorType'}
											sx={{
												width: { sm: 340, lg: 368 },
											}}
											initialValue={doctorType}
											error={isDoctorInvalid || errorDoctorType}
										/>
										{errorDoctorType && (
											<Typography variant="bodyLink" color={palette.error.main}>
												{tClientLabels(`newClaim.prescription.doctorTypeError`)}
											</Typography>
										)}
										{member?.rxChangeConfiguration.doctorWarningControl && (
											<Stack direction={'row'} gap={'8px'}>
												<InfoIcon width={'18px'} style={{ minWidth: '18px' }} />
												<Typography variant={'value2'} pt={'2px'}>
													{tDbLabels(
														`newClaim.doctorWarningInput.${member?.rxChangeConfiguration.allowedDoctors
															.map(el => el.code)
															.sort()
															.join('.')}`,
													)}
												</Typography>
											</Stack>
										)}
									</Stack>
									<Stack gap={'8px'}>
										<FormControl
											sx={{ gap: '8px', width: { sm: 340, lg: 368 }, marginLeft: 2 }}
										>
											<DatePicker
												variant="margin"
												label={tClientLabels('newClaim.prescription.dateOfPrescription')}
												value={dayjs(prescriptionDate).utcOffset(0)}
												onChange={value => {
													setErrorDate(false);
													setPrescriptionDate(value);
													setFormData((prev: IClaim) => ({
														...prev,
														newPrescriptionContent: {
															...prev.newPrescriptionContent,
															prescriptionDate: formatZuluForBE(value),
														} as IPrescription,
													}));
												}}
												error={isDateInvalid}
											/>
											{errorDate && (
												<Typography variant="bodyLink" color={palette.error.main}>
													{tClientLabels(`newClaim.prescription.${errorDateMessage}`)}
												</Typography>
											)}
										</FormControl>
										{member?.rxChangeConfiguration.prescriptionWarningControl &&
											member?.rxChangeConfiguration.validityPrescriptionMonth !== null && (
												<Stack direction={'row'} ml={'16px'} gap={'8px'}>
													<InfoIcon width={'18px'} />
													<Typography variant={'value2'} pt={'2px'}>
														{tClientLabels('newClaim.prescription.info', {
															prescriptionValidity:
																member?.rxChangeConfiguration.validityPrescriptionMonth,
														})}
													</Typography>
												</Stack>
											)}
									</Stack>
								</Stack>
							</Stack>
							{member?.rxChangeConfiguration.doctorCheckSiteControl ? (
								<Stack direction={'row'} gap={'6px'}>
									<Typography
										variant={'body'}
										sx={{ paddingBottom: isDesktop ? '2px' : '0' }}
									>
										{tClientLabels('newClaim.checkODValidity.message')}
									</Typography>
									<ButtonLink
										label={tClientLabels('newClaim.checkODValidity.link')}
										to={'https://albounico.fnomceo.it/'}
										sx={{
											width: 'fit-content',
											'.MuiTypography-root': { textTransform: 'none !important' },
										}}
									/>
								</Stack>
							) : null}
						</Stack>
						<Prescription
							isOldPrescription={false}
							presFilled={newPresCompleted}
							key="new"
							label={''}
							data={formData.newPrescriptionContent!}
							setData={(value: IPrescription) => {
								setFormData((prev: IClaim) => ({
									...prev,
									newPrescriptionContent: value,
								}));
							}}
							checkFields={checkFields}
							setRequiredFilled={setNewPresCompleted}
							setErrorPresent={setNewPresErrorPresent}
						/>
						{checkFields && (newPresErrorPresent || oldPresErrorPresent) ? (
							<Typography
								variant="bodyLink"
								sx={theme => ({
									color: theme.palette.error.main,
								})}
								mt={1}
							>
								{tClientLabels(`newClaim.prescription.errorFields`)}
							</Typography>
						) : (
							<></>
						)}
						<Stack
							sx={{
								mt: '12px',
								left: '-8px',
								position: 'relative',
								whiteSpace: 'break-spaces',
							}}
						>
							<Checkbox
								label={tClientLabels('newClaim.prescription.prescriptionConsent')}
								checked={!!formData.prescriptionConsent}
								onChange={(_, value) => {
									setFormData((prev: IClaim) => ({
										...prev,
										prescriptionConsent: value,
									}));
								}}
							></Checkbox>
						</Stack>
					</>
				)}
				{isRxCheckAvoided || doctorType !== 'reset' || prescriptionDate !== null ? (
					<NewClaimErrorMessages
						doctorType={doctorType}
						prescriptionDate={
							prescriptionDate ? dayjs(prescriptionDate).toDate() : null
						}
						isRxCheckAvoided={!!isRxCheckAvoided}
					/>
				) : (
					<></>
				)}
			</Stack>
			<Box alignItems="start" mt="30px">
				<Button
					disabled={disableProceed}
					variant="contained"
					sx={{ mt: '10px' }}
					onClick={handleSubmit}
				>
					{tClientLabels('newClaim.selectedPage.proceed')}
				</Button>
			</Box>
		</>
	);
};

export default Page1;
